/**
 * Turn grouping for the transcript (R18-1/R18-2) — fold the flat entry list
 * into one group per player input, derived ENTIRELY from data the engine
 * already commits.
 *
 * @remarks
 * Entry ids are deterministic namespaces (architecture.md §7.3): `input-<n>-*`
 * for an input's own lines (echo first, at `input-<n>-0`), `turn-<t>-*` for
 * the world's answers to its executed clauses, `session-start-*` for the
 * opening emission. Chronology guarantees a turn's entries follow their
 * input's echo, so the fold needs one rule: **a new group opens when an
 * entry's id names a DIFFERENT input namespace; everything else continues the
 * open group.** No engine change, no schema change — the seams were always in
 * the ids.
 *
 * The group's heading is the player's echo when it has one (Mike's R18
 * ruling: the typed words alone — the seam the eye already uses becomes the
 * heading a screen reader jumps by). Groups without an echo (the session
 * opening; a blank input's responses) get a generated label the component
 * renders visually hidden.
 *
 * Relocated from `player-ui` in R20 (D86): the transcript EXPORT needs the
 * same fold, and two consumers means the engine owns the derivation — the
 * `deriveJournal`/`deriveMapGraph` precedent. The UI renders it; the
 * serializer reads it; neither can drift on what a "turn" is.
 *
 * @packageDocumentation
 */

import type { RuntimeTranscriptEntry } from '@orpheus/content-schema';

/** One input's worth of transcript, ready to render as a labeled section. */
export interface TranscriptTurnGroup {
	/** The group's lines, in order — the heading echo EXCLUDED when present. */
	readonly entries: readonly RuntimeTranscriptEntry[];
	/** The player echo serving as the group's visible heading, when the group has one. */
	readonly headingEntry?: RuntimeTranscriptEntry;
	/** The heading text: the echo's words, or the generated label for echo-less groups. */
	readonly headingText: string;
	/** Stable render key (the group's namespace). */
	readonly key: string;
}

/** The id-namespace key that OPENS a group, or `undefined` for entries that continue the current one. */
const groupKeyOf = (entryId: string): string | undefined => {
	const inputMatch = /^input-(?<ordinal>\d+)-/u.exec(entryId);

	if (inputMatch?.groups?.['ordinal'] !== undefined) {
		return `input-${inputMatch.groups['ordinal']}`;
	}

	return entryId.startsWith('session-start-') ? 'session-start' : undefined;
};

/** The generated label for a group whose first entry is not a player echo. */
const generatedLabelOf = (groupKey: string): string => (groupKey === 'session-start' ? 'Session start' : 'Blank input');

/**
 * Fold the transcript into turn groups, oldest first.
 *
 * @param entries - The transcript, exactly as the engine committed it
 * @returns The groups, in order (empty for an empty transcript)
 */
export const groupTranscriptEntries = (entries: readonly RuntimeTranscriptEntry[]): readonly TranscriptTurnGroup[] => {
	const groups: { entries: RuntimeTranscriptEntry[]; headingEntry?: RuntimeTranscriptEntry; headingText: string; key: string }[] = [];

	for (const entry of entries) {
		const entryGroupKey = groupKeyOf(entry.id);
		const openGroup = groups[groups.length - 1];

		if (openGroup !== undefined && (entryGroupKey === undefined || entryGroupKey === openGroup.key)) {
			openGroup.entries.push(entry);
			continue;
		}

		// A turn-* entry with NO open group cannot occur in engine output (turns
		// run inside inputs), but the fold is total anyway: it opens the session
		// group rather than inventing a rule the data cannot express.
		const groupKey = entryGroupKey ?? 'session-start';
		const isEchoHeading = entry.source === 'player';

		groups.push({
			entries: isEchoHeading ? [] : [entry],
			...(isEchoHeading ? { headingEntry: entry } : {}),
			headingText: isEchoHeading ? entry.text : generatedLabelOf(groupKey),
			key: groupKey,
		});
	}

	return groups;
};
